import { createContext, useContext, useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { initFirebase } from '../firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { auth, db } = initFirebase();
  const [user, loading, error] = useAuthState(auth);
  const [userRole, setUserRole] = useState(null);
  const [customerIds, setCustomerIds] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user && user.email) {
        setRoleLoading(true);
        try {
          const userDoc = await getDoc(doc(db, 'users', user.email.toLowerCase()));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserRole(data.role || 'end_customer');
            setCustomerIds(data.customerIds || []);
          } else {
            setUserRole('end_customer');
            setCustomerIds([]);
          }
        } catch (err) {
          console.error('Error fetching user role:', err);
          setUserRole('end_customer');
          setCustomerIds([]);
        } finally {
          setRoleLoading(false);
        }
      } else {
        setUserRole(null);
        setCustomerIds([]);
      }
    };

    fetchUserRole();
  }, [user, db]);

  const isBusinessOwner = userRole === 'business_owner';
  const isEndCustomer = userRole === 'end_customer';

  const value = {
    user,
    loading: loading || roleLoading,
    error,
    userRole,
    customerIds,
    isBusinessOwner,
    isEndCustomer,
    auth,
    db,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
